import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { chatsApi } from "@/lib/humhub-api/chats";
import type { ChatRoomResponse } from "@/lib/humhub-api/types";

/**
 * チャットルーム検索・フィルタリング用のフック
 * 全てのチャットルームを取得してから検索処理を行う
 */
export const useChatRoomsForFilter = () => {
  const {
    data: firstPageData,
    isLoading: isLoadingFirstPage,
    error: firstPageError,
    refetch: refetchFirstPage,
  } = useQuery({
    queryKey: ["chatRoomsForFilter", "firstPage"],
    queryFn: () => chatsApi.getChatRooms(1, 50),
    staleTime: 2 * 60 * 1000, // 検索用なので少し長めのキャッシュ
  });

  // 追加ページが必要かどうかを判定
  const needsAdditionalPages = useMemo(() => {
    if (!firstPageData) return false;
    return firstPageData.pages > 1;
  }, [firstPageData]);

  // 追加ページのデータを取得（必要な場合のみ）
  const {
    data: additionalPagesData,
    isLoading: isLoadingAdditionalPages,
    error: additionalPagesError,
  } = useQuery({
    queryKey: ["chatRoomsForFilter", "additionalPages", firstPageData?.pages],
    queryFn: async () => {
      if (!firstPageData || firstPageData.pages <= 1) {
        return [];
      }

      // 2ページ目以降を順次取得
      const promises = [];
      for (let page = 2; page <= firstPageData.pages; page++) {
        promises.push(chatsApi.getChatRooms(page, 1000));
      }

      const results = await Promise.all(promises);
      return results.flatMap((result) => result.results);
    },
    enabled: needsAdditionalPages,
    staleTime: 2 * 60 * 1000,
  });

  // 全てのチャットルームを結合
  const allChatRooms = useMemo(() => {
    const firstPageRooms = firstPageData?.results || [];
    const additionalRooms = additionalPagesData || [];
    return [...firstPageRooms, ...additionalRooms];
  }, [firstPageData?.results, additionalPagesData]);

  // 全てのチャットルームIDを取得
  const allChatRoomIds = useMemo(() => {
    return allChatRooms.map((room) => room.id.toString());
  }, [allChatRooms]);

  // ローディング状態
  const isLoading = isLoadingFirstPage || (needsAdditionalPages && isLoadingAdditionalPages);

  // エラー状態
  const error = firstPageError || additionalPagesError;

  // データが完全に取得できているかどうか
  const isDataComplete = useMemo(() => {
    if (isLoading || error) return false;
    if (!firstPageData) return false;
    if (needsAdditionalPages && !additionalPagesData) return false;
    return true;
  }, [isLoading, error, firstPageData, needsAdditionalPages, additionalPagesData]);

  // リフェッチ関数
  const refetch = useCallback(async () => {
    const result = await refetchFirstPage();
    return result;
  }, [refetchFirstPage]);

  // キーワードでチャットルームをフィルタリング
  const filterChatRooms = useCallback(
    (keyword: string): ChatRoomResponse[] => {
      if (!keyword.trim()) return allChatRooms;

      const searchTerm = keyword.toLowerCase();
      return allChatRooms.filter(
        (room) =>
          room.title.toLowerCase().includes(searchTerm) ||
          room.display_title?.toLowerCase().includes(searchTerm),
      );
    },
    [allChatRooms],
  );

  return {
    // データ
    allChatRooms,
    allChatRoomIds,
    totalCount: firstPageData?.total || 0,

    // 状態
    isLoading,
    isDataComplete,
    error: error ? String(error) : null,

    // メソッド
    refetch,
    filterChatRooms,
  };
};
